import { defineStore } from 'pinia'
import type RouteWaypoint from '@/types/RouteWaypoint.ts'
import { useRouteStore } from '@/stores/routeStore.ts'
import { useMapStore } from '@/stores/mapStore.ts'
import Backend from '@/models/backend.ts'
import { pointToLineDistance } from '@/utils/geometry.ts'

export const useRouteEditorStore = defineStore('routeEditorStore', {
  state: () => ({
    initialized: false,
    editingRouteId: null as number | null,
    routeName: '',
    waypoints: [] as RouteWaypoint[],
    draggingWaypointIndex: null as number | null,
    redrawPath: false,
    lastCreatedRouteId: null as number | null,
    pendingNewRoute: false,
    pendingWaypoint: null as { x: number; y: number } | null
  }),
  getters: {
    isEditing: (state): boolean => state.editingRouteId !== null,
    isValidRouteName: (state): boolean => {
      // Route name must contain at least one letter (not just numbers/spaces/symbols)
      return /[a-zA-Z]/.test(state.routeName)
    },
    canSave(): boolean {
      return this.waypoints.length >= 2 && this.routeName.trim().length > 0 && this.isValidRouteName
    },
    routeNameError(): string {
      if (this.routeName.trim().length === 0) return ''
      if (!this.isValidRouteName) return 'Route name must contain at least one letter'
      return ''
    },
    waypointCount: (state): number => state.waypoints.length
  },
  actions: {
    startNewRoute() {
      const routeStore = useRouteStore()
      this.initialized = true
      this.editingRouteId = null
      this.routeName = routeStore.getNextRouteName
      this.waypoints = []
      this.pendingWaypoint = null
      this.redrawPath = true

      // Close sidebar and tooltip
      const mapStore = useMapStore()
      mapStore.mapSideBarVisible = false
      mapStore.toolTipVisible = false
    },
    startEditRoute(routeId: number, focusOnRoute: boolean = false) {
      const routeStore = useRouteStore()
      const route = routeStore.findRouteById(routeId)
      if (!route) return

      this.initialized = true
      this.editingRouteId = routeId
      this.routeName = route.name
      this.waypoints = [...route.waypoints]
      this.draggingWaypointIndex = null
      this.redrawPath = true

      // Close sidebar and tooltip
      const mapStore = useMapStore()
      mapStore.mapSideBarVisible = false
      mapStore.toolTipVisible = false

      // Only focus on first waypoint if requested (e.g., from sidebar)
      if (focusOnRoute && route.waypoints.length > 0) {
        mapStore.focusMap(route.waypoints[0].x, route.waypoints[0].y)
      }
    },
    addWaypoint(x: number, y: number) {
      this.waypoints.push({ x, y })
      this.pendingWaypoint = null
      this.redrawPath = true
    },
    updateWaypoint(index: number, x: number, y: number) {
      if (index >= 0 && index < this.waypoints.length) {
        this.waypoints[index] = { x, y }
        this.redrawPath = true
      }
    },
    removeWaypoint(index: number) {
      if (index >= 0 && index < this.waypoints.length) {
        this.waypoints.splice(index, 1)
        this.redrawPath = true
      }
    },
    removeLastWaypoint() {
      if (this.waypoints.length > 0) {
        this.waypoints.pop()
        this.redrawPath = true
      }
    },
    startDraggingWaypoint(index: number) {
      this.draggingWaypointIndex = index
    },
    stopDraggingWaypoint() {
      this.draggingWaypointIndex = null
    },
    findWaypointNear(x: number, y: number, radius: number = 3): number {
      // Returns index of waypoint near position, or -1 if none found
      for (let i = 0; i < this.waypoints.length; i++) {
        const wp = this.waypoints[i]
        const distance = Math.sqrt(Math.pow(wp.x - x, 2) + Math.pow(wp.y - y, 2))
        if (distance <= radius) {
          return i
        }
      }
      return -1
    },
    findLineSegmentNear(x: number, y: number, threshold: number = 3): number {
      // Returns the index of the first waypoint of the segment clicked, or -1 if none
      // If returns i, the click was between waypoints[i] and waypoints[i+1]
      if (this.waypoints.length < 2) return -1

      for (let i = 0; i < this.waypoints.length - 1; i++) {
        const p1 = this.waypoints[i]
        const p2 = this.waypoints[i + 1]
        const distance = pointToLineDistance(x, y, p1.x, p1.y, p2.x, p2.y)
        if (distance <= threshold) {
          return i
        }
      }
      return -1
    },
    insertWaypointAfter(index: number, x: number, y: number) {
      // Insert a new waypoint after the given index
      if (index >= 0 && index < this.waypoints.length) {
        this.waypoints.splice(index + 1, 0, { x, y })
        this.redrawPath = true
      }
    },
    setRouteName(name: string) {
      this.routeName = name
    },
    async save() {
      if (!this.canSave) return

      const backend = new Backend()
      const isNewRoute = this.editingRouteId === null

      if (isNewRoute) {
        // Create new route - set flag so we can track when it arrives via WebSocket
        this.pendingNewRoute = true
        await backend.createRoute(this.routeName, this.waypoints)
      } else {
        // Update existing route
        await backend.updateRoute(this.editingRouteId!, this.routeName, this.waypoints)
      }

      this.reset()
    },
    setLastCreatedRouteId(routeId: number) {
      this.lastCreatedRouteId = routeId
      this.pendingNewRoute = false
    },
    cancel() {
      this.reset()
    },
    reset() {
      this.initialized = false
      this.editingRouteId = null
      this.routeName = ''
      this.waypoints = []
      this.draggingWaypointIndex = null
      this.redrawPath = true
      // Note: lastCreatedRouteId and pendingNewRoute are intentionally NOT reset here
      // They are managed by setLastCreatedRouteId() and cleared manually
    },
    clearLastCreatedRoute() {
      this.lastCreatedRouteId = null
      this.pendingNewRoute = false
    }
  }
})
